import { Pagination } from "@mui/material";
import { DownloadIcon } from "@radix-ui/react-icons";
import { BarCode01Icon } from "hugeicons-react";
import { MoreHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import model from "../../assets/model-fichier/model-numeroFtth.xlsx";
import { Loader } from "../../components/Widget/Loader";
import { UploadButton } from "../../components/Widget/UploadButton";
import clientAxios from "../../services/axios";
import { store } from "../../services/service";
import { useGlobalStore } from "../../store/store";
import {
  BASE_URL,
  ENREGISTRER_NUMERO_FFTH,
  EXPORTER_NUMERO_FTTH,
  IMPORTER_NUMERO_FTTH,
  LISTE_NUMERO_FTTH,
  MODIFICATION_NUMERO_FTTH,
  SUPPRIMER_NUMERO_FTTH,
} from "../../Utils/constant";
import { checkTagValidity, userToken } from "../../Utils/Utils";

const statusConfig = {
  LIBRE: { label: "Libre", cls: "bg-green-100 text-green-700" },
  RESERVER: { label: "Réservé", cls: "bg-blue-100 text-blue-700" },
  EN_ATTENTE_LIBERATION: {
    label: "Att. libération",
    cls: "bg-orange-100 text-orange-700",
  },
  EN_ATTENTE_ACTIVATION: {
    label: "Att. activation",
    cls: "bg-yellow-100 text-yellow-700",
  },
  ALLOUER: { label: "Alloué", cls: "bg-purple-100 text-purple-700" },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || {
    label: status?.replace(/_/g, " ").toLowerCase(),
    cls: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
};

export const NumeroFtth = () => {
  const numeroFtth = useGlobalStore();
  const [selectItem, setSelectItem] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statut, setStatut] = useState("LIBRE");
  const [plageDebut, setPlageDebut] = useState("");
  const [plageFin, setPlageFin] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [pid, setPid] = useState(null);
  const [profil, setprofil] = useState("RESIDENTIEL");
  const refresh = (params = "page=0&param=&status=&size=40") =>
    numeroFtth.getData(`${LISTE_NUMERO_FTTH}?${params}`);

  const handleDownload = () => {
    setLoading(true);
    fetch(
      `${BASE_URL + EXPORTER_NUMERO_FTTH}?plageDebut=${plageDebut}&statut=${statut}&plageFin=${plageFin}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      },
    )
      .then((res) => res.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "LISTE_NUMERO_FTTH.xlsx";
        document.body.appendChild(a);
        a.click();
        setLoading(false);
        document.getElementById("export").close();
      })
      .catch(() => setLoading(false));
  };

  const handleEdit = () => {
    setIsSubmit(true);
    store(
      `${MODIFICATION_NUMERO_FTTH}/${selectItem?.id}?status=${statut}&pid=${pid}&typeNumero=${profil}`,
    )
      .then(() => {
        setIsSubmit(false);
        refresh();
        toast.success("Modification réussie");
        document.getElementById("update").close();
      })
      .catch((err) => {
        setIsSubmit(false);
        toast.error(err.response.data.donnee);
        document.getElementById("update").close();
      });
  };

  const submit = (data) => {
    setIsSubmit(true);
    store(
      `${ENREGISTRER_NUMERO_FFTH}?plageDebut=${Number(data.plageDebut)}&plageFin=${Number(data.plageFin)}`,
      "application/json",
      {},
    )
      .then((res) => {
        setIsSubmit(false);
        if (res.status === 200) {
          reset();
          document.getElementById("add").close();
          toast.success("Numéros générés avec succès");
          refresh();
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        setMsg(err.response.data.donnee);
      });
  };

  useEffect(() => {
    refresh();
  }, []);

  const more = (page) => refresh(`page=${page - 1}&param=&status=&size=40`);

  const inputCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const selectCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";
  const closeBtnCls =
    "w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-semibold transition";
  const submitBtnCls = (loading) =>
    `w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${
      loading
        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
        : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
    }`;

  if (!checkTagValidity("LISTE_NUMERO_FTTH")) return null;

  return (
    <div>
      {/* Header */}
      <div className="mt-4 mb-1">
        <p className="text-2xl font-bold text-gray-900 tracking-tight">
          Numéros FTTH
        </p>
        <p className="text-sm text-gray-400 mt-1">
          Liste des numéros FTTH disponibles pour les abonnements clients
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 py-4 border-b border-gray-100 mb-6">
        {/* Search */}
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 h-10">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9ca3af"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            onChange={(e) =>
              refresh(`page=0&param=${e.target.value}&status=&size=40`)
            }
            type="text"
            placeholder="Rechercher un numéro…"
            className="bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400 w-48"
          />
        </div>

        {/* Status filter */}
        <select
          className="h-10 px-3 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition"
          onChange={(e) =>
            refresh(`page=0&param=&status=${e.target.value}&size=40`)
          }
        >
          <option value="">Tous les statuts</option>
          <option value="LIBRE">Libre</option>
          <option value="RESERVER">Réservé</option>
          <option value="EN_ATTENTE_LIBERATION">
            En attente de libération
          </option>
          <option value="EN_ATTENTE_ACTIVATION">En attente d'activation</option>
          <option value="ALLOUER">Alloué</option>
        </select>

        <div className="ml-auto flex items-center gap-2">
          <UploadButton
            uploadUrl={IMPORTER_NUMERO_FTTH}
            fetchData={() => refresh()}
            model={model}
          />

          {checkTagValidity("EXPORTER_NUMERO_FTTH") && (
            <button
              onClick={() => document.getElementById("export").showModal()}
              className="h-10 px-4 flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium rounded-xl transition"
            >
              <DownloadIcon />
              Exporter
            </button>
          )}

          {checkTagValidity("ENREGISTRER_NUMERO_FTTH") && (
            <button
              onClick={() => {
                reset();
                setMsg("");
                document.getElementById("add").showModal();
              }}
              className="h-10 px-4 bg-yellow-400 hover:bg-yellow-300 text-gray-900 text-sm font-semibold rounded-xl transition"
            >
              Générer des numéros
            </button>
          )}
        </div>
      </div>

      {/* Export Modal */}
      <dialog id="export" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Exporter les numéros FTTH
            </h2>
            <form method="dialog">
              <button className={closeBtnCls}>✕</button>
            </form>
          </div>
          {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Plage début
              </label>
              <input
                type="text"
                className={inputCls}
                onChange={(e) => setPlageDebut(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Plage fin
              </label>
              <input
                type="text"
                className={inputCls}
                onChange={(e) => setPlageFin(e.target.value)}
              />
            </div>
            <button
              onClick={handleDownload}
              disabled={loading}
              className={submitBtnCls(loading)}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-xs" /> Export
                  en cours…
                </>
              ) : (
                "Exporter la liste"
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* Generate Modal */}
      <dialog id="add" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Générer des numéros FTTH
            </h2>
            <form method="dialog">
              <button className={closeBtnCls}>✕</button>
            </form>
          </div>
          {msg && <p className="text-xs text-red-500 mb-3">{msg}</p>}
          <form onSubmit={handleSubmit(submit)} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Plage début <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inputCls}
                {...register("plageDebut", { required: true })}
              />
              {errors.plageDebut && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Plage fin <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className={inputCls}
                {...register("plageFin", { required: true })}
              />
              {errors.plageFin && (
                <p className="text-xs text-red-500 mt-1">
                  Ce champ est obligatoire
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmit}
              className={submitBtnCls(isSubmit)}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Génération…
                </>
              ) : (
                "Générer"
              )}
            </button>
          </form>
        </div>
      </dialog>

      {/* Update Modal */}
      <dialog id="update" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Modifier le numéro
            </h2>
            <form method="dialog">
              <button className={closeBtnCls}>✕</button>
            </form>
          </div>

          {selectItem && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4">
              <p className="text-xs text-gray-500">Numéro</p>
              <p className="text-sm font-semibold text-gray-900 mt-0.5">
                {selectItem?.numero}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                PID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={pid ?? ""}
                onChange={(e) => setPid(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Profil
              </label>
              <select
                className={selectCls}
                value={profil}
                onChange={(e) => setprofil(e.target.value)}
              >
                <option value="RESIDENTIEL">Residentiel</option>
                <option value="BUSINESS_PREPAID">Business prepaid</option>
                <option value="BUSINESS_POSTPAID">Business postpaid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Statut
              </label>
              <select
                className={selectCls}
                onChange={(e) => setStatut(e.target.value)}
              >
                <option value="LIBRE">Libre</option>
                <option value="RESERVER">Réservé</option>
              </select>
            </div>
            <button
              onClick={handleEdit}
              disabled={isSubmit}
              className={submitBtnCls(isSubmit)}
            >
              {isSubmit ? (
                <>
                  <span className="loading loading-spinner loading-xs" />{" "}
                  Modification…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </div>
      </dialog>

      {/* Delete Modal */}
      <dialog id="delete" className="modal">
        <div className="modal-box rounded-2xl p-6 max-w-md">
          <p className="text-base font-bold text-gray-900 mb-1">
            Confirmer la suppression
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Le numéro{" "}
            <span className="font-semibold text-gray-800">
              {selectItem?.numero}
            </span>{" "}
            sera définitivement supprimé.
          </p>
          <div className="flex justify-end gap-2">
            <form method="dialog">
              <button className="h-9 px-4 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition">
                Annuler
              </button>
            </form>
            <button
              onClick={() => {
                setIsDeleting(true);
                clientAxios
                  .delete(`${SUPPRIMER_NUMERO_FTTH}/${selectItem.id}`)
                  .then(() => {
                    refresh();
                    toast.success("Numéro supprimé");
                    document.getElementById("delete").close();
                  })
                  .catch((err) =>
                    toast.error(
                      err?.response?.data?.donnee ||
                        "Erreur lors de la suppression",
                    ),
                  )
                  .finally(() => setIsDeleting(false));
              }}
              disabled={isDeleting}
              className={`h-9 px-4 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                isDeleting
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-red-500 hover:bg-red-600 text-white"
              }`}
            >
              {isDeleting && (
                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              {isDeleting ? "Suppression…" : "Supprimer"}
            </button>
          </div>
        </div>
      </dialog>

      {/* Grid */}
      {numeroFtth?.loading ? (
        <Loader />
      ) : numeroFtth?.data?.donnee?.data?.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <p className="text-sm text-gray-400">Aucun numéro FTTH trouvé</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3">
          {numeroFtth?.data?.donnee?.data?.map((item) => (
            <div
              key={item.id}
              className="relative bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3 hover:shadow-md hover:border-yellow-300 transition-all duration-200"
            >
              {/* Menu */}
              <div className="dropdown dropdown-end absolute top-2 right-2">
                <div
                  tabIndex={0}
                  role="button"
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition cursor-pointer"
                >
                  <MoreHorizontal size={15} />
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content z-10 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg w-40 p-1.5"
                >
                  <li>
                    <button
                      className="w-full h-8 px-3 text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      onClick={() => {
                        setSelectItem(item);
                        setPid(item?.pid);
                        setprofil(item?.typeNumero);
                        document.getElementById("update").showModal();
                      }}
                    >
                      Modifier
                    </button>
                  </li>
                  <li>
                    <button
                      className="w-full h-8 px-3 text-left text-xs font-medium text-red-600 rounded-lg hover:bg-red-50 transition"
                      onClick={() => {
                        setSelectItem(item);
                        document.getElementById("delete").showModal();
                      }}
                    >
                      Supprimer
                    </button>
                  </li>
                </ul>
              </div>

              {/* Icon */}
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 text-gray-500">
                <BarCode01Icon size={18} />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 pt-0.5 pr-6">
                <p className="text-xs font-semibold text-gray-900 truncate">
                  {item?.numero}
                </p>
                {item?.pid && (
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    PID: {item.pid}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mt-1.5">
                    <StatusBadge status={item?.status} />
                  </div>
                  {item?.typeNumero && (
                    <span className="truncate font-medium bg-gray-50 border border-gray-200  px-3 py-1 rounded-full text-xs text-gray-400 ">
                      {item.typeNumero.replace(/_/g, " ").toLowerCase()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-end mt-6">
        <Pagination
          onChange={(_, v) => more(v)}
          count={numeroFtth.data?.donnee?.totalPages}
          variant="outlined"
          color="primary"
          shape="rounded"
          size="small"
        />
      </div>
    </div>
  );
};
